import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/**
 * Minimum gap between two focus-triggered revalidations. Alt-tabbing fires
 * `visibilitychange` and `focus` back to back, so without a floor a user
 * flipping between windows would issue a `getMe` per flip.
 */
export const SESSION_REVALIDATE_MIN_INTERVAL_MS = 30_000;

type ShouldRevalidateArgs = {
  hasToken: boolean;
  isOnline: boolean;
  lastRevalidatedAt: number | null;
  now: number;
  minIntervalMs?: number;
};

/**
 * Pure gate for the focus revalidation (TA-2593).
 *
 * Deliberately conservative — the app has a history of spurious logouts
 * (TA-10286), so this only ever decides whether to *ask* the server. It never
 * decides that a session is invalid; that stays with the errorLink 401 path.
 */
export const shouldRevalidateSession = ({
  hasToken,
  isOnline,
  lastRevalidatedAt,
  now,
  minIntervalMs = SESSION_REVALIDATE_MIN_INTERVAL_MS,
}: ShouldRevalidateArgs): boolean => {
  if (!hasToken) return false;
  // Offline requests fail as network errors, which errorLink correctly ignores
  // — so the round trip would be pure noise.
  if (!isOnline) return false;
  if (lastRevalidatedAt !== null && now - lastRevalidatedAt < minIntervalMs) {
    return false;
  }
  return true;
};

/**
 * Re-verify the stored session with the server when a web tab regains focus.
 *
 * TA-2593 (VAPT): a session revoked elsewhere — most importantly by a password
 * reset — is only discovered when the client next talks to the API. A tab left
 * open in the background can otherwise keep rendering from the persisted Apollo
 * cache long after its token stopped being valid. Revalidating on focus bounds
 * that window to "the next time the user looks at the tab".
 *
 * The revoked-token *decision* is not made here: `revalidate` goes through the
 * normal Apollo link chain, and only a genuine HTTP 401 (not a network error,
 * not a 5xx, not a permission-scoped 401) reaches the logout path in
 * `useApolloClient`'s errorLink.
 */
export const useRevalidateSessionOnFocus = ({
  hasToken,
  revalidate,
}: {
  hasToken: boolean;
  revalidate: () => Promise<unknown>;
}): void => {
  const lastRevalidatedAtRef = useRef<number | null>(null);
  const revalidateRef = useRef(revalidate);
  revalidateRef.current = revalidate;

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    if (!hasToken) {
      // Next login starts with a clean throttle rather than inheriting the
      // previous session's timestamp.
      lastRevalidatedAtRef.current = null;
      return;
    }

    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;

      const now = Date.now();
      if (
        !shouldRevalidateSession({
          hasToken: true,
          isOnline: window.navigator?.onLine ?? true,
          lastRevalidatedAt: lastRevalidatedAtRef.current,
          now,
        })
      ) {
        return;
      }
      lastRevalidatedAtRef.current = now;

      // Swallowed on purpose: this hook never logs anyone out itself.
      Promise.resolve(revalidateRef.current()).catch(() => undefined);
    };

    // `visibilitychange` covers tab switches and minimising; `focus` covers
    // switching between windows where the tab stays "visible". Both share the
    // throttle above, so the pair costs at most one request per interval.
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [hasToken]);
};
